import axios from 'axios';
import { RetryHandler, RateLimiter, OfflineQueue, RequestCache, RequestDeduplicator, PersistentCache } from './ApiUtils';

const BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/+$/, '');
const API_V1 = `${BASE_URL}/api/v1`;
const AUTH_URL = `${API_V1}/auth`;

// Exported so other modules (e.g. trackingSocket.js) can derive a ws(s):// URL
// from the same configured host instead of re-reading REACT_APP_API_URL.
export const API_BASE_URL = BASE_URL;

const CACHE        = new RequestCache();
const DEDUPLICATOR = new RequestDeduplicator();
const OFFLINE_QUEUE = new OfflineQueue();
const RATE_LIMITER = new RateLimiter(100, 60000);
const PERSISTENT   = new PersistentCache();

// Server-side legal gate: a 403 LEGAL_ACKNOWLEDGEMENT_REQUIRED on any
// business endpoint re-opens the LegalGate mid-session (a new mandatory
// version was published). Tokens are NOT cleared — this is not an auth error.
let legalRequiredCallback = null;
export const setLegalRequiredCallback = (cb) => { legalRequiredCallback = cb; };
export const resetLegalRequiredHandler = () => { legalRequiredCallback = null; };

// Reconnect polling — fires every 20s when server is unreachable, clears on success.
let _reconnectTimer = null;
function _startReconnectPolling(axiosInstance) {
    if (_reconnectTimer) return;
    _reconnectTimer = setInterval(async () => {
        try {
            await axiosInstance.get('/organization/roles/', { params: { page_size: 1 } });
            clearInterval(_reconnectTimer);
            _reconnectTimer = null;
            window.dispatchEvent(new CustomEvent('tas:server:online'));
        } catch (_) { /* still offline */ }
    }, 20000);
}

// Also clear reconnect polling on native browser online event
window.addEventListener('online', () => {
    if (_reconnectTimer) {
        clearInterval(_reconnectTimer);
        _reconnectTimer = null;
    }
    window.dispatchEvent(new CustomEvent('tas:server:online'));
});

// Per-endpoint cache TTLs (ms). Stable reference data lives longer in cache;
// frequently-changing data gets a short or zero TTL.
const ENDPOINT_TTL = {
    '/organization/states/':          10 * 60 * 1000, // 10 min
    '/organization/branches/':        10 * 60 * 1000,
    '/organization/areas/':           10 * 60 * 1000,
    '/organization/roles/':            5 * 60 * 1000, //  5 min
    '/organization/users/':            2 * 60 * 1000, //  2 min
    '/features/':                      5 * 60 * 1000,
    '/allowance/requests/':           30 * 1000,      // 30 s
    '/attendance/corrections/':       30 * 1000,
    '/tracking/':                          0,          // never cache live data
    '/tracking/route/':                    0,
    '/livetracking/':                      0,          // never cache route/session payloads — a previous
                                                       // day's response must never outlive a date change
};

let isRefreshing = false;
let failedQueue = [];
let isLoggingOut = false;

const getAccessToken = () => sessionStorage.getItem('access_token');
const getRefreshToken = () => sessionStorage.getItem('refresh_token');

// Device ID Configuration for Desktop & Mobile App
// ================================================

// Get platform type (WEB, ANDROID, IOS)
const getPlatform = () => {
    const nav = window.navigator;
    const userAgent = nav.userAgent.toLowerCase();
    
    if (/android/i.test(userAgent)) return 'ANDROID';
    if (/iphone|ipad|ios/i.test(userAgent)) return 'IOS';
    if (/mobile|webapp/i.test(userAgent)) return 'MOBILE';
    return 'WEB';
};

const PLATFORM = getPlatform();
const isMobileApp = PLATFORM === 'ANDROID' || PLATFORM === 'IOS' || PLATFORM === 'MOBILE';

// Generate unique ID for Web (Desktop)
const generateWebDeviceId = () => {
    const nav = window.navigator;
    const screen = window.screen;
    
    // Combine stable browser properties
    const fingerprint = [
        nav.userAgent,
        nav.language,
        nav.platform,
        nav.hardwareConcurrency || '1',
        screen.width,
        screen.height,
        screen.colorDepth,
        screen.pixelDepth,
        Intl.DateTimeFormat().resolvedOptions().timeZone
    ].join('|');
    
    // Deterministic hash
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }
    
    return 'web_' + Math.abs(hash).toString(16);
};

// Main device ID getter
const getDeviceId = () => {
    // For Mobile App - use Expo/SecureStore device ID (app should handle this)
    if (isMobileApp) {
        // Mobile app should set this before login
        let mobileDeviceId = localStorage.getItem('mobile_device_id');
        if (mobileDeviceId) return mobileDeviceId;
        
        // Fallback: generate from device info
        const nav = window.navigator;
        const fingerprint = [
            nav.userAgent,
            nav.deviceMemory || '4',
            nav.hardwareConcurrency || '4',
            nav.platform
        ].join('|');
        
        let hash = 0;
        for (let i = 0; i < fingerprint.length; i++) {
            const char = fingerprint.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        
        mobileDeviceId = PLATFORM.toLowerCase() + '_' + Math.abs(hash).toString(16);
        localStorage.setItem('mobile_device_id', mobileDeviceId);
        return mobileDeviceId;
    }
    
    // For Desktop/Web - use localStorage (persistent)
    let deviceId = localStorage.getItem('device_fingerprint');
    if (!deviceId) {
        deviceId = generateWebDeviceId();
        localStorage.setItem('device_fingerprint', deviceId);
    }
    
    return deviceId;
};

// Get device ID for API calls with platform prefix
const getApiDeviceId = () => {
    const deviceId = getDeviceId();
    // Return with platform prefix if not already present
    if (isMobileApp && !deviceId.startsWith(PLATFORM.toLowerCase())) {
        return PLATFORM.toLowerCase() + '_' + deviceId;
    }
    return deviceId;
};

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) prom.reject(error);
        else prom.resolve(token);
    });
    failedQueue = [];
};

class ScalableAPI {
    constructor() {
        this.api = axios.create({
            baseURL: API_V1,
            timeout: 30000,
            headers: { 'Content-Type': 'application/json' }
        });

        this.setupInterceptors();
    }

    setupInterceptors() {
        this.api.interceptors.request.use(async (config) => {
            await RATE_LIMITER.acquire();
            
            const token = getAccessToken();
            if (token && !config.url.includes('/auth/token')) {
                config.headers.Authorization = `Bearer ${token}`;
            }

            const sessionToken = sessionStorage.getItem('session_token');
            if (sessionToken) {
                config.headers['X-Session-Token'] = sessionToken;
            }

            config.headers['X-DEVICE-ID'] = getDeviceId();
            config.headers['X-PLATFORM'] = 'WEB';
            config.headers['X-USER-AGENT'] = navigator.userAgent || '';

            return config;
        });

        this.api.interceptors.response.use(
            res => res,
            async (error) => {
                const originalRequest = error.config;

                if (!error.response) {
                    if (error.request && !navigator.onLine) {
                        OFFLINE_QUEUE.add({
                            url: originalRequest.url,
                            method: originalRequest.method,
                            data: originalRequest.data,
                            params: originalRequest.params
                        });
                    }
                    return Promise.reject({ ...error, offline: true });
                }

                if (error.response.status === 403 &&
                    error.response.data?.code === 'LEGAL_ACKNOWLEDGEMENT_REQUIRED') {
                    // Not an auth failure — do NOT clear tokens. Tell the gate
                    // to re-check /legal/status/ and take over the UI.
                    if (legalRequiredCallback) {
                        try { legalRequiredCallback(); } catch (_) { /* gate handles its own errors */ }
                    }
                }

                if (originalRequest.url.includes('/auth/token/refresh/')) {
                    this.logout('Your session has expired. Please log in again.');
                    return Promise.reject(error);
                }

                if (error.response.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/token/')) {
                    if (isLoggingOut) return Promise.reject(error);
                    originalRequest._retry = true;
                    const refresh = getRefreshToken();

                    if (!refresh) {
                        this.logout('Your session has expired. Please log in again.');
                        return Promise.reject(error);
                    }

                    if (isRefreshing) {
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject });
                        }).then(token => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            return this.api(originalRequest);
                        });
                    }

                    isRefreshing = true;

                    try {
                        const res = await axios.post(`${AUTH_URL}/token/refresh/`, { refresh });
                        const newAccess = res.data.access;

                        sessionStorage.setItem('access_token', newAccess);
                        if (res.data.session_token) {
                            sessionStorage.setItem('session_token', res.data.session_token);
                        }
                        this.api.defaults.headers.Authorization = `Bearer ${newAccess}`;
                        processQueue(null, newAccess);
                        originalRequest.headers.Authorization = `Bearer ${newAccess}`;

                        return this.api(originalRequest);
                    } catch (err) {
                        processQueue(err, null);
                        this.logout('Your session has expired. Please log in again.');
                        return Promise.reject(err);
                    } finally {
                        isRefreshing = false;
                    }
                }

                if (error.response?.status === 403) {
                    const errorCode = error.response?.data?.code;
                    
                    if (errorCode === 'DEVICE_NOT_BINDED' || errorCode === 'DEVICE_ID_REQUIRED') {
                        localStorage.removeItem('device_id');
                        this.logout();
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    static getInstance() {
        if (!ScalableAPI.instance) {
            ScalableAPI.instance = new ScalableAPI();
        }
        return ScalableAPI.instance;
    }

    async get(url, params = {}, options = {}) {
        const { retry = true } = options;
        // Resolve effective TTL: explicit option > per-endpoint default > global default (30s)
        const explicitTTL = options.cacheTTL ?? (options.useCache === false ? 0 : undefined);
        const endpointTTL = Object.entries(ENDPOINT_TTL).find(([k]) => url.includes(k))?.[1];
        const ttl = explicitTTL ?? endpointTTL ?? CACHE.duration;
        const useCache = ttl > 0;
        // `persistentCache: false` opts an endpoint out of BOTH persistent-cache
        // duties: it is not written, and it is not replayed as a stale offline
        // fallback. Route endpoints use this, because replaying a stored route
        // against a new date/employee/session silently draws a route that
        // belongs to a different journey.
        const persist = options.persistentCache !== false;

        const cacheKey = `${url}_${JSON.stringify(params)}`;

        if (useCache) {
            const cached = CACHE.get(cacheKey);
            if (cached) return { data: cached, cached: true };
        }

        // Deduplicate: identical in-flight GET → one network request, shared promise
        return DEDUPLICATOR.execute(cacheKey, async () => {
            const request = async () => {
                const res = await this.api.get(url, { params });
                if (useCache) CACHE.set(cacheKey, res.data, ttl);
                if (persist) PERSISTENT.set(cacheKey, res.data); // latest successful response
                window.dispatchEvent(new CustomEvent('tas:server:online'));
                if (_reconnectTimer) { clearInterval(_reconnectTimer); _reconnectTimer = null; }
                return res;
            };
            try {
                return retry ? await RetryHandler.withRetry(request) : await request();
            } catch (err) {
                // Server unreachable — serve last known-good data from persistent cache
                if (persist && (!err.response || err.offline)) {
                    const stale = PERSISTENT.get(cacheKey);
                    if (stale) {
                        window.dispatchEvent(new CustomEvent('tas:server:offline', {
                            detail: { cachedAt: stale.timestamp },
                        }));
                        _startReconnectPolling(this.api);
                        return { data: stale.data, stale: true, cachedAt: stale.timestamp };
                    }
                }
                if (!err.response || err.offline) {
                    window.dispatchEvent(new CustomEvent('tas:server:offline', { detail: {} }));
                    _startReconnectPolling(this.api);
                }
                throw err;
            }
        });
    }

    async post(url, data = {}, options = {}) {
        const { retry = false, offline = false } = options;

        const request = async () => {
            const res = await this.api.post(url, data);
            this.invalidateCache(url);
            return res;
        };

        if (offline && !navigator.onLine) {
            OFFLINE_QUEUE.add({ url, method: 'POST', data, params: {} });
            return { data: { queued: true }, offline: true };
        }

        if (retry) {
            return RetryHandler.withRetry(request);
        }
        return request();
    }

    async patch(url, data = {}, options = {}) {
        const { retry = false } = options;

        const request = async () => {
            const res = await this.api.patch(url, data);
            this.invalidateCache(url);
            return res;
        };

        if (retry) {
            return RetryHandler.withRetry(request);
        }
        return request();
    }

    async put(url, data = {}) {
        const res = await this.api.put(url, data);
        this.invalidateCache(url);
        return res;
    }

    async delete(url) {
        const res = await this.api.delete(url);
        this.invalidateCache(url);
        return res;
    }

    invalidateCache(prefix = null) {
        if (prefix) {
            CACHE.invalidatePrefix(prefix);
        } else {
            CACHE.invalidate();
        }
    }

    async processOfflineQueue() {
        if (navigator.onLine && OFFLINE_QUEUE.length > 0) {
            await OFFLINE_QUEUE.process(this);
        }
    }

    async login(username, password) {
        const deviceId = getApiDeviceId();
        
        const res = await axios.post(`${AUTH_URL}/token/`, { username, password }, {
            headers: {
                'X-DEVICE-ID': deviceId,
                'X-PLATFORM': PLATFORM,
                'X-USER-AGENT': navigator.userAgent || '',
            }
        });

        const data = res.data;

        sessionStorage.setItem('access_token', data.access);
        sessionStorage.setItem('refresh_token', data.refresh);

        if (data.session_token) {
            sessionStorage.setItem('session_token', data.session_token);
        }

        if (data.user) {
            sessionStorage.setItem('user', JSON.stringify(data.user));
        }

        if (data.device) {
            sessionStorage.setItem('device_info', JSON.stringify(data.device));
        }

        CACHE.invalidate();

        return data;
    }

logout(reason) {
        if (isLoggingOut) return;
        isLoggingOut = true;

        // Captured before clearing — sent to /logout/ below so the backend
        // can actively blacklist it. Without this, "logout" only marked the
        // server-side UserSession row inactive; the JWT refresh token
        // itself stayed valid for its full lifetime and could still be
        // replayed to mint new access tokens after a user "logged out".
        const refreshToken = sessionStorage.getItem('refresh_token');

        // Clear tokens immediately
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('session_token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('device_id');
        sessionStorage.removeItem('device_info');
        CACHE.invalidate();

        // Persist reason across the hard redirect so Login.jsx can display it.
        // Only set for involuntary logouts (token expiry, idle timeout) — manual
        // logout passes no reason so the login page shows clean with no banner.
        if (reason) {
            localStorage.setItem('logout_reason', reason);
        }

        // Notify React synchronously so ProtectedRoute sees user=null before any redirect.
        // This prevents the change-password screen from flashing when a session expires
        // while must_change_password was true — the two flows are entirely separate.
        window.dispatchEvent(new CustomEvent('auth:force-logout'));

        // Backend notification + redirect (async — page reload finalises cleanup)
        const deviceId = getApiDeviceId();
        if (deviceId && navigator.onLine) {
            axios.post(`${AUTH_URL}/logout/`, { refresh: refreshToken }, {
                headers: {
                    'X-DEVICE-ID': deviceId,
                    'X-PLATFORM': PLATFORM,
                }
            }).then(() => {
                isLoggingOut = false;
                window.location.replace('/#/login');
            }).catch(() => {
                isLoggingOut = false;
                window.location.replace('/#/login');
            });
        } else {
            isLoggingOut = false;
            window.location.replace('/#/login');
        }
    }
    
    getDeviceId() {
        return getApiDeviceId();
    }
    
    getPlatform() {
        return PLATFORM;
    }

    async getCurrentUser() {
        const token = getAccessToken();
        if (!token) {
            return Promise.reject('No token');
        }
        // retry:false — this runs on every page load before anything else
        // renders; AuthContext already re-checks automatically once
        // tas:server:online fires, so retrying here just makes a down
        // server take ~90s (3 attempts) to show ServerMaintenance instead of ~30s.
        return this.get('/organization/users/me/', {}, { retry: false });
    }

    createUser(data) {
        return this.post('/organization/users/', data);
    }

    updateUser(id, data) {
        return this.patch(`/organization/users/${id}/`, data);
    }

    deleteUser(id, reason) {
        return this.post(`/organization/users/${id}/soft_delete/`, { reason });
    }

    // Permanent — only allowed on an already-deactivated account (see
    // hard_delete's docstring on the backend). Frees the employee_id/
    // username for reuse, unlike deleteUser() above.
    hardDeleteUser(id, reason) {
        return this.post(`/organization/users/${id}/hard_delete/`, { reason });
    }

    getUsers(params = {}, options = {}) {
        return this.get('/organization/users/', params, options);
    }

    getOrgUserStats(options = {}) {
        return this.get('/organization/users/stats/', {}, options);
    }

    getUser(id) {
        return this.get(`/organization/users/${id}/`);
    }

    changePassword(password) {
        return this.post('/organization/users/change_my_password/', { password });
    }

    resetUserPassword(userId, newPassword) {
        return this.post(`/organization/users/${userId}/reset_password/`, { new_password: newPassword });
    }

    blockUser(userId, reason) {
        return this.post(`/organization/users/${userId}/block/`, { reason });
    }

    unblockUser(userId) {
        return this.post(`/organization/users/${userId}/unblock/`, {});
    }

    getUserActivityLog(userId) {
        return this.get(`/organization/users/${userId}/activity_log/`);
    }

    createPunchRecord(data) {
        return this.post('/attendance/punches/', data, { offline: true });
    }

    getDailySummary(params = {}) {
        return this.get('/attendance/punches/daily_summary/', params);
    }

    getOrgDailySummary(params = {}, options = {}) {
        return this.get('/attendance/punches/org_daily_summary/', params, options);
    }

    getTodayPunches() {
        return this.get('/attendance/punches/today_punches/');
    }

    getPunchRecords(params = {}, options = {}) {
        return this.get('/attendance/punches/', params, options);
    }

    // Reverse-geocode via the backend proxy (keeps the Google API key server-side).
    reverseGeocode(lat, lng) {
        return this.get('/attendance/geocode/', { latlng: `${lat},${lng}` });
    }

    // ── Location exceptions (out-of-range punch review) ──
    getLocationExceptionsSummary(params = {}) {
        return this.get('/attendance/punches/location_exceptions_summary/', params);
    }

    getLocationExceptions(params = {}) {
        return this.get('/attendance/punches/location_exceptions/', params);
    }

    getLocationExceptionsPendingCount() {
        return this.get('/attendance/punches/location_exceptions_pending_count/');
    }

    reviewLocationException(id, decision, comment = '') {
        return this.post(`/attendance/punches/${id}/review/`, { decision, comment });
    }

    deleteLocationException(id) {
        return this.delete(`/attendance/punches/${id}/`);
    }

    getCorrections(params = {}) {
        return this.get('/attendance/corrections/', params);
    }

    getCorrectionsPendingCount() {
        return this.get('/attendance/corrections/pending_count/');
    }

    createCorrection(data) {
        return this.post('/attendance/corrections/', data);
    }

    getEmployeeTracking() {
        return this.get('/tracking/employees/');
    }

    getEmployeeActivity(employeeId) {
        return this.get(`/tracking/employees/${employeeId}/activity/`);
    }

    getEmployeeRoute(employeeId, params = {}) {
        return this.get(`/tracking/employees/${employeeId}/route/`, params);
    }

    // ================= FORGOT PUNCH VERIFICATION (Milestone 2a) =================
    getForgotPunchRequests(params = {}) {
        return this.get('/punch-verification/requests/', params);
    }

    getForgotPunchRequestDetail(id) {
        return this.get(`/punch-verification/requests/${id}/`);
    }

    getForgotPunchRoute(id) {
        return this.get(`/punch-verification/requests/${id}/route/`);
    }

    getForgotPunchNearby(id, params = {}) {
        return this.get(`/punch-verification/requests/${id}/nearby/`, params);
    }

    reviewForgotPunchRequest(id, data) {
        return this.post(`/punch-verification/requests/${id}/review/`, data);
    }

    getForgotPunchPendingCount() {
        return this.get('/punch-verification/requests/pending_count/');
    }

    createAllowanceRequest(data) {
        return this.post('/allowance/requests/', data);
    }

    getAllowanceRequests(params = {}) {
        return this.get('/allowance/requests/', params);
    }

    approveAllowanceRequest(id) {
        return this.post(`/allowance/requests/${id}/approve/`);
    }

    getPendingApprovals(params = {}) {
        return this.get('/allowance/requests/', { status: 'PENDING', ...params });
    }

    getPendingApprovalsCount() {
        return this.get('/allowance/requests/pending_count/');
    }

    rejectAllowanceRequest(id, data) {
        return this.post(`/allowance/requests/${id}/reject/`, data);
    }

    getLoanVisits(params = {}) {
        return this.get('/loans/visits/', params);
    }

    getCollectionUpdateStats(params = {}) {
        return this.get('/loans/collection-updates/stats/', params);
    }

    getCollectionEmployeeSummary(params = {}) {
        return this.get('/loans/collection-updates/employee_summary/', params);
    }

    // ── Collection Correction Requests ──
    getCollectionCorrections(params = {}) {
        return this.get('/loans/collection-corrections/', params);
    }

    getCollectionCorrection(id) {
        return this.get(`/loans/collection-corrections/${id}/`);
    }

    respondCollectionCorrection(id, decision, comments = '') {
        return this.post(`/loans/collection-corrections/${id}/respond/`, { decision, comments });
    }

    getCollectionCorrectionsPendingCount() {
        return this.get('/loans/collection-corrections/pending_count/');
    }

    getCollectionCorrectionTimeline(id) {
        return this.get(`/loans/collection-corrections/${id}/timeline/`);
    }

    // ── Collection & Activity report (apps.reporting) ──
    getActivityKpis(params = {}) {
        return this.get('/reporting/activity/kpis/', params);
    }

    getActivityEmployeeSummary(params = {}) {
        return this.get('/reporting/activity/employee_summary/', params);
    }

    getActivityEmployeeDetail(params = {}) {
        return this.get('/reporting/activity/employee_detail/', params);
    }

    getActivityTimeline(params = {}) {
        return this.get('/reporting/activity/employee_timeline/', params);
    }

    getActivityRoute(params = {}) {
        return this.get('/reporting/activity/route/', params, { useCache: false });
    }

    exportActivityReport(params = {}) {
        return this.api.get('/reporting/activity/export/', { params, responseType: 'blob' });
    }

    getProfileRequests(params = {}) {
        return this.get('/organization/profile-update/', params);
    }

    getProfilePendingCount() {
        return this.get('/organization/profile-update/pending_count/');
    }

    approveProfileRequest(id, data = {}) {
        return this.post(`/organization/profile-update/${id}/approve/`, data);
    }

    rejectProfileRequest(id, data = {}) {
        return this.post(`/organization/profile-update/${id}/reject/`, data);
    }

    // ── Customer collection records ──
    getCollections(params = {}, options = {}) {
        return this.get('/loans/collections/', params, options);
    }

    getCollection(id) {
        return this.get(`/loans/collections/${id}/`);
    }

    getLoanCollectionPayments(loanId) {
        return this.get('/loans/collection-payments/', { loan_id: loanId }, { useCache: false });
    }

    async uploadCollections(file, collectionType = 'REGULAR', { refreshFinancials = false } = {}) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('collection_type', collectionType);
        // Opt-in: re-apply corrected reference figures to already-uploaded loans.
        if (refreshFinancials) formData.append('refresh_financials', 'true');
        // Large files take longer than the default 30s to parse + upsert. Uses
        // this.api directly (not the wrapped post()) so it can set a custom
        // timeout/multipart header — which means it must invalidate the cache
        // itself below; the wrapped post() would have done this automatically.
        const res = await this.api.post('/loans/collections/upload/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 180000,
        });
        this.invalidateCache('/loans/collections');
        return res;
    }

    async clearCollections(collectionType = null) {
        const res = await this.post('/loans/collections/clear/', collectionType ? { collection_type: collectionType } : {});
        // post() already invalidates the '/loans/collections/clear/' cache key,
        // but that never matches the list/summary endpoints' own cache keys —
        // bust those explicitly so the table reflects the clear immediately.
        this.invalidateCache('/loans/collections');
        return res;
    }

    async clearCollectionsByEmployee(employeeId) {
        const res = await this.post('/loans/collections/clear/', { employee_id: employeeId });
        this.invalidateCache('/loans/collections');
        return res;
    }

    async deleteCollectionsByIds(recordIds) {
        const res = await this.post('/loans/collections/clear/', { record_ids: recordIds });
        this.invalidateCache('/loans/collections');
        return res;
    }

    async bulkAssignCollections(collectionIds, employeeId, reason = '') {
        const res = await this.post('/loans/collections/bulk_assign/', {
            collection_ids: collectionIds, employee_id: employeeId, reason,
        });
        this.invalidateCache('/loans/collections');
        return res;
    }

    // ── Collections Display Reset (non-destructive MTD/dashboard reset) ──
    getCollectionsDisplayReset() {
        return this.get('/loans/collections/display-reset/', {}, { useCache: false });
    }

    async clearCollectionsDisplayNow() {
        const res = await this.post('/loans/collections/display-reset/clear-now/', {});
        this.invalidateCache('/loans/collections');
        return res;
    }

    async configureCollectionsAutoReset(data) {
        const res = await this.post('/loans/collections/display-reset/configure/', data);
        this.invalidateCache('/loans/collections');
        return res;
    }

    getCollectionSummary(options = {}) {
        return this.get('/loans/collections/summary/', {}, options);
    }

    getCollectionFilteredSummary(params = {}) {
        return this.get('/loans/collections/filtered_summary/', params);
    }

    // ── Collection Plan Report ──
    getCollectionDpdSummary(params = {}) {
        return this.get('/loans/collections/dpd_summary/', params);
    }

    getDistinctStates() {
        return this.get('/loans/collections/distinct_states/', {}, { useCache: true });
    }

    // Bypasses the cached get() wrapper — a CSV download needs
    // responseType: 'blob', not JSON (same reason as exportDuplicateCollections).
    exportCollectionPlanReport(params = {}) {
        return this.api.get('/loans/collections/export/', {
            params,
            responseType: 'blob',
        });
    }

    getCollectionDashboardStats(options = {}) {
        return this.get('/loans/collections/dashboard_stats/', {}, options);
    }

    getCollectionPlanStats(params, options = {}) {
        // get(url, params, options) takes the query params directly as its
        // second argument (it wraps them in { params } for axios internally)
        // — passing { params } here double-wrapped them into a literal
        // "params" query key instead of the real query string.
        return this.get('/loans/collections/employee_plan_stats/', params, options);
    }

    getCollectionUpdates(params = {}, options = {}) {
        return this.get('/loans/collection-updates/', params, options);
    }

    getVisitTrend(params = {}, options = {}) {
        return this.get('/loans/collection-updates/visit_trend/', params, options);
    }

    async updateCollectionStatus(id, data) {
        const res = await this.post(`/loans/collections/${id}/update_status/`, data);
        // Same cache-key mismatch as clearCollections — bust the list/summary
        // cache explicitly so a status change shows up immediately.
        this.invalidateCache('/loans/collections');
        return res;
    }

    // ── Duplicate Collection Records (Super Admin only) ──
    getDuplicateCollections(params = {}) {
        return this.get('/loans/collection-duplicates/', params);
    }

    getDuplicateCollection(id) {
        return this.get(`/loans/collection-duplicates/${id}/`);
    }

    compareDuplicateCollection(id) {
        return this.get(`/loans/collection-duplicates/${id}/compare/`);
    }

    async deleteDuplicateCollection(id) {
        const res = await this.api.delete(`/loans/collection-duplicates/${id}/`);
        this.invalidateCache('/loans/collection-duplicates');
        return res;
    }

    async bulkDeleteDuplicateCollections(ids) {
        const res = await this.api.post('/loans/collection-duplicates/bulk_delete/', { ids });
        this.invalidateCache('/loans/collection-duplicates');
        return res;
    }

    // Bypasses the cached get() wrapper (same reason as uploadCollections) —
    // a CSV download needs responseType: 'blob', not JSON.
    exportDuplicateCollections(params = {}) {
        return this.api.get('/loans/collection-duplicates/export/', {
            params,
            responseType: 'blob',
        });
    }

    // ── Upload Batch (Upload History / Active Upload Management) ────────────
    getUploadBatches(params = {}) {
        return this.get('/loans/upload-batches/', params);
    }

    getUploadBatch(id) {
        return this.get(`/loans/upload-batches/${id}/`);
    }

    getArchivedUploadBatches(params = {}) {
        return this.get('/loans/upload-batches/archived/', params);
    }

    async removeUploadBatchFromUpload(id, reason) {
        const res = await this.post(`/loans/upload-batches/${id}/remove_from_upload/`, { reason });
        this.invalidateCache('/loans/upload-batches');
        return res;
    }

    async completeDeleteUploadBatch(id, reason, confirmationText) {
        const res = await this.post(`/loans/upload-batches/${id}/complete_delete/`, {
            reason, confirmation_text: confirmationText,
        });
        this.invalidateCache('/loans/upload-batches');
        this.invalidateCache('/loans/collections');
        return res;
    }

    // Retroactive fix for the currency-symbol amount-parsing bug (e.g.
    // "₹5,040" that an older upload silently stored as amount_due=0):
    // re-reads this batch's own stored file and corrects only the rows that
    // bug actually broke. dryRun=true (default) only reports what would
    // change; pass false to write it.
    async repairUploadBatchAmounts(id, dryRun = true) {
        const res = await this.post(`/loans/upload-batches/${id}/repair_amounts/`, { dry_run: dryRun });
        if (!dryRun) {
            this.invalidateCache('/loans/upload-batches');
            this.invalidateCache('/loans/collections');
        }
        return res;
    }

    // ── Data Sync (MSSQL / PostgreSQL / SQLite → CollectionRecord) ──────────
    getDataSyncConfigs(params = {}) {
        return this.get('/datasync/configs/', params, { useCache: false });
    }

    getDataSyncConfig(id) {
        return this.get(`/datasync/configs/${id}/`, {}, { useCache: false });
    }

    createDataSyncConfig(data) {
        return this.post('/datasync/configs/', data);
    }

    updateDataSyncConfig(id, data) {
        return this.patch(`/datasync/configs/${id}/`, data);
    }

    deleteDataSyncConfig(id) {
        return this.delete(`/datasync/configs/${id}/`);
    }

    testDataSyncConnection(data) {
        return this.post('/datasync/configs/test-connection/', data, { timeout: 30000 });
    }

    triggerDataSync(configId) {
        return this.post('/datasync/configs/sync/', { config_id: configId }, { timeout: 300000 });
    }

    getDataSyncStatus(logId, configId) {
        const params = {};
        if (logId) params.log_id = logId;
        if (configId) params.config_id = configId;
        return this.get('/datasync/configs/status/', params, { useCache: false });
    }

    getDataSyncedData(configId, params = {}) {
        return this.get(`/datasync/configs/${configId}/synced-data/`, params, { useCache: false });
    }

    getDataSyncInterval() {
        return this.get('/datasync/configs/interval/', {}, { useCache: false });
    }

    updateDataSyncInterval(intervalHours) {
        return this.patch('/datasync/configs/interval/', { interval_hours: intervalHours });
    }

    getDataSyncLogs(params = {}) {
        return this.get('/datasync/logs/', params, { useCache: false });
    }

    getDataSyncLog(id) {
        return this.get(`/datasync/logs/${id}/`, {}, { useCache: false });
    }

    // ── Visit Planner ─────────────────────────────────────────────────────────
    getDistinctProducts() {
        return this.get('/loans/collections/distinct_products/', {}, { cacheTTL: 5 * 60 * 1000 });
    }

    getDistinctProductTypes() {
        return this.get('/loans/collections/distinct_product_types/', {}, { cacheTTL: 5 * 60 * 1000 });
    }

    getDistinctBranchNames() {
        return this.get('/loans/collections/distinct_branch_names/', {}, { cacheTTL: 5 * 60 * 1000 });
    }

    // ── Customer Assignment Module ──
    getRecommendedEmployees(collectionId) {
        return this.get(`/loans/collections/${collectionId}/recommended_employees/`);
    }

    getAssignableEmployees(collectionId, search = '') {
        return this.get(`/loans/collections/${collectionId}/assignable_employees/`, { search });
    }

    // Type-ahead for pickers — server-side, <=10 rows, limited to people the viewer may see.
    employeeAutocomplete(q, params = {}) {
        return this.get('/organization/users/autocomplete/', { q, ...params }, { useCache: false });
    }

    // One case: current owner, ownership periods, employee-wise breakdown and a
    // paginated timeline (?page &page_size &type &employee_id &order).
    getCaseActivity(collectionId, params = {}) {
        return this.get(`/loans/collections/${collectionId}/case_activity/`, params, { useCache: false });
    }

    getAssignmentHistory(collectionId) {
        return this.get(`/loans/collections/${collectionId}/assignment_history/`);
    }

    assignCustomer(collectionId, employeeId, reason = '') {
        return this.post(`/loans/collections/${collectionId}/assign/`, { employee_id: employeeId, reason });
    }

    bulkAssignCustomers(collectionIds, employeeId, reason = '') {
        return this.post('/loans/collections/bulk_assign/', {
            collection_ids: collectionIds, employee_id: employeeId, reason,
        });
    }

    bulkTransferCollections(data, queryParams = {}) {
        return this.post('/loans/collections/bulk_transfer/', { ...data, ...queryParams }).then((r) => {
            this.invalidateCache('/loans/collections');
            return r;
        });
    }

    getAssignmentStats() {
        return this.get('/loans/collections/assignment_stats/');
    }

    triggerGeocoding() {
        return this.post('/loans/collections/trigger_geocoding/', {});
    }

    getVisitPlans(params = {}) {
        return this.get('/loans/visit-plans/', params, { useCache: false });
    }

    getTodayVisitPlan(params = {}) {
        return this.get('/loans/visit-plans/today/', params, { useCache: false });
    }

    createVisitPlan(data) {
        return this.post('/loans/visit-plans/', data);
    }

    updateVisitPlan(id, data) {
        return this.patch(`/loans/visit-plans/${id}/`, data);
    }

    addPlanItems(planId, collectionIds) {
        return this.post(`/loans/visit-plans/${planId}/add_items/`, { collection_ids: collectionIds });
    }

    removePlanItem(planId, collectionId) {
        return this.post(`/loans/visit-plans/${planId}/remove_item/`, { collection_id: collectionId });
    }

    reorderPlanItems(planId, itemIdOrder) {
        return this.post(`/loans/visit-plans/${planId}/reorder/`, { order: itemIdOrder });
    }

    activateVisitPlan(planId) {
        return this.post(`/loans/visit-plans/${planId}/activate/`);
    }

    completeVisitPlan(planId) {
        return this.post(`/loans/visit-plans/${planId}/complete/`);
    }

    // ── Customer Map ──
    // Viewport marker data is volatile and hierarchy-scoped per requester —
    // never cached, same reasoning as getVisitPlans/getTodayVisitPlan above.
    getMapMarkers(bounds, params = {}) {
        return this.get('/loans/collections/map_markers/', { ...bounds, ...params }, { useCache: false });
    }

    getMapDetail(id, params = {}) {
        return this.get(`/loans/collections/${id}/map_detail/`, params, { useCache: false });
    }

    getMapEta(id, userLat, userLng) {
        return this.get(`/loans/collections/${id}/eta/`, { user_lat: userLat, user_lng: userLng }, { useCache: false });
    }

    getMapSearch(query, params = {}) {
        return this.get('/loans/collections/map_search/', { q: query, ...params }, { useCache: false });
    }

    getNearbyEmployees(params = {}) {
        return this.get('/loans/collections/nearby_employees/', params, { useCache: false });
    }

    getTeamOverview(params = {}) {
        return this.get('/loans/collections/team_overview/', params, { useCache: false });
    }

    getMapSummary(bounds, params = {}) {
        return this.get('/loans/collections/map_summary/', { ...bounds, ...params }, { useCache: false });
    }

    getDistinctRegions() {
        return this.get('/loans/collections/distinct_regions/', {}, { cacheTTL: 5 * 60 * 1000 });
    }

    getDistinctAreas() {
        return this.get('/loans/collections/distinct_areas/', {}, { cacheTTL: 5 * 60 * 1000 });
    }

    // q= narrows to centers whose combined "CODE- Name" string contains it
    // (either half) and caps the result — for a search-with-suggestions
    // box. Omit q for the full list (e.g. the Customer Map's Center filter).
    getDistinctCenters(q = '') {
        const params = q ? { q } : {};
        return this.get('/loans/collections/distinct_centers/', params, { cacheTTL: 5 * 60 * 1000 });
    }

    setRiskCategory(id, riskCategory) {
        return this.post(`/loans/collections/${id}/set_risk_category/`, { risk_category: riskCategory });
    }

    getRoles(options = { useCache: true }) {
        return this.get('/organization/roles/', {}, options);
    }

    createRole(data) {
        return this.post('/organization/roles/', data);
    }

    updateRole(id, data) {
        return this.patch(`/organization/roles/${id}/`, data);
    }

    deleteRole(id) {
        return this.delete(`/organization/roles/${id}/`);
    }

    getStates(options = { useCache: true }) {
        return this.get('/organization/states/', {}, options);
    }

    getBranches(state, options = { useCache: true }) {
        return this.get('/organization/branches/', { state }, options);
    }

    getAreas(branch, options = { useCache: true }) {
        return this.get('/organization/areas/', { branch }, options);
    }

    // ── Organization Settings: State/Zonal/Area/Branch Master (hierarchy RBAC) ──
    createState(data) {
        return this.post('/organization/states/', data).then(r => { this.invalidateCache('/organization/states'); return r; });
    }
    updateState(id, data) {
        return this.api.patch(`/organization/states/${id}/`, data).then(r => { this.invalidateCache('/organization/states'); return r; });
    }

    getOrgZones(state) {
        return this.get('/organization/org-zones/', state ? { state } : {}, { useCache: false });
    }
    createOrgZone(data) {
        return this.post('/organization/org-zones/', data).then(r => { this.invalidateCache('/organization/org-zones'); return r; });
    }
    updateOrgZone(id, data) {
        return this.api.patch(`/organization/org-zones/${id}/`, data).then(r => { this.invalidateCache('/organization/org-zones'); return r; });
    }

    getManagementAreas(zone) {
        return this.get('/organization/management-areas/', zone ? { zone } : {}, { useCache: false });
    }
    createManagementArea(data) {
        return this.post('/organization/management-areas/', data).then(r => { this.invalidateCache('/organization/management-areas'); return r; });
    }
    updateManagementArea(id, data) {
        return this.api.patch(`/organization/management-areas/${id}/`, data).then(r => { this.invalidateCache('/organization/management-areas'); return r; });
    }

    // ── Feature Access Control (hierarchy RBAC ScopeAssignment) ──
    getActions() {
        return this.get('/permissions/actions/', {}, { useCache: true });
    }
    getScopeAssignments(params = {}) {
        return this.get('/permissions/scope-assignments/', params, { useCache: false });
    }
    createScopeAssignment(data) {
        return this.post('/permissions/scope-assignments/', data);
    }
    updateScopeAssignment(id, data) {
        return this.api.patch(`/permissions/scope-assignments/${id}/`, data);
    }
    revokeScopeAssignment(id, reason) {
        return this.post(`/permissions/scope-assignments/${id}/revoke/`, { reason });
    }
    getAccessAuditLog(params = {}) {
        return this.get('/audit/logs/', { table_name: 'ScopeAssignment', ...params }, { useCache: false });
    }
    getRoleDataScopePolicies() {
        return this.get('/permissions/role-data-scope-policies/', {}, { useCache: false });
    }
    updateRoleDataScopePolicy(roleId, data) {
        return this.api.patch(`/permissions/role-data-scope-policies/${roleId}/`, data);
    }

    // ── Enterprise Access Control — field-level + audit + case transfer ──
    getModuleFields(params = {}) {
        return this.get('/permissions/module-fields/', params, { useCache: false });
    }
    createModuleField(data) {
        return this.post('/permissions/module-fields/', data);
    }
    updateModuleField(id, data) {
        return this.api.patch(`/permissions/module-fields/${id}/`, data);
    }
    deleteModuleField(id) {
        return this.api.delete(`/permissions/module-fields/${id}/`);
    }
    getFieldPolicies(params = {}) {
        return this.get('/permissions/field-policies/', params, { useCache: false });
    }
    createFieldPolicy(data) {
        return this.post('/permissions/field-policies/', data);
    }
    updateFieldPolicy(id, data) {
        return this.api.patch(`/permissions/field-policies/${id}/`, data);
    }
    deleteFieldPolicy(id) {
        return this.api.delete(`/permissions/field-policies/${id}/`);
    }
    getAccessAuditLogs(params = {}) {
        return this.get('/permissions/access-audit-logs/', params, { useCache: false });
    }
    getActivityAuditLogs(params = {}) {
        return this.get('/permissions/activity-audit-logs/', params, { useCache: false });
    }
    getCaseTransfers(params = {}) {
        return this.get('/permissions/case-transfers/', params, { useCache: false });
    }
    createCaseTransfer(data) {
        return this.post('/permissions/case-transfers/', data);
    }
    approveCaseTransfer(id, data = {}) {
        return this.post(`/permissions/case-transfers/${id}/approve/`, data);
    }
    rejectCaseTransfer(id, data = {}) {
        return this.post(`/permissions/case-transfers/${id}/reject/`, data);
    }

    createBranch(data) {
        return this.post('/organization/branches/', data).then(r => { this.invalidateCache('/organization/branches'); return r; });
    }
    updateBranch(id, data) {
        return this.api.patch(`/organization/branches/${id}/`, data).then(r => { this.invalidateCache('/organization/branches'); return r; });
    }
    deleteBranch(id) {
        return this.api.delete(`/organization/branches/${id}/`).then(r => { this.invalidateCache('/organization/branches'); return r; });
    }
    wipeAllBranches() {
        return this.post('/organization/branches/wipe-all/').then(r => { this.invalidateCache('/organization/branches'); return r; });
    }

    // ── Village / City Master (Organization Settings) ──
    getVillagesCities(branch, options = { useCache: false }) {
        return this.get('/organization/village-cities/', branch ? { branch } : {}, options);
    }
    createVillageCity(data) {
        return this.post('/organization/village-cities/', data).then(r => { this.invalidateCache('/organization/village-cities'); return r; });
    }
    updateVillageCity(id, data) {
        return this.api.patch(`/organization/village-cities/${id}/`, data).then(r => { this.invalidateCache('/organization/village-cities'); return r; });
    }

    // ── Center Master (Organization Settings) ──
    // The list endpoint is server-side paginated and returns
    // {results, total_count, page, page_size, total_pages, next, previous},
    // so every caller must pass page/page_size through rather than filtering
    // client-side. useCache:false for the same reason every other org master
    // uses it — this is a volatile master-data list behind a custom page size.
    getCenters(params = {}, options = { useCache: false }) {
        return this.get('/organization/centers/', params, options);
    }
    getCenter(id) {
        return this.get(`/organization/centers/${id}/`, {}, { useCache: false });
    }
    createCenter(data) {
        return this.post('/organization/centers/', data).then(r => { this.invalidateCache('/organization/centers'); return r; });
    }
    updateCenter(id, data) {
        return this.api.patch(`/organization/centers/${id}/`, data).then(r => { this.invalidateCache('/organization/centers'); return r; });
    }
    // Master data is never hard-deleted — DELETE is a soft deactivate server
    // side, and this is the explicit, idempotent row action the table uses.
    deactivateCenter(id) {
        return this.api.post(`/organization/centers/${id}/deactivate/`, {}).then(r => { this.invalidateCache('/organization/centers'); return r; });
    }
    reactivateCenter(id) {
        return this.api.post(`/organization/centers/${id}/reactivate/`, {}).then(r => { this.invalidateCache('/organization/centers'); return r; });
    }

    // Center uploads land in the SAME MasterDataUploadBatch registry as
    // Region/Branch/Village-City, so the existing error-report download
    // endpoint works unchanged; only the history LIST method was missing
    // (it was never surfaced in the UI for any master-data resource).
    getMasterDataUploadBatches(params = {}) {
        return this.get('/organization/upload-batches/', params, { useCache: false });
    }

    // ── Org Structure bulk upload (Region / Branch / Village-City) — shared
    // validate -> confirm flow, one set of methods parameterized by the
    // resource's URL segment rather than three near-duplicate blocks. ──
    _masterUploadBase(resource) {
        return resource === 'regions' ? `/hierarchy/${resource}` : `/organization/${resource}`;
    }

    downloadMasterTemplate(resource) {
        return this.api.get(`${this._masterUploadBase(resource)}/template/`, { responseType: 'blob' });
    }

    async validateMasterUpload(resource, file) {
        const formData = new FormData();
        formData.append('file', file);
        // Mirrors uploadCollections: bypasses the cached post() wrapper for a
        // custom timeout + multipart header on a potentially large sheet.
        //
        // 180s is ample now that validate/confirm are ASYNCHRONOUS: both only
        // store the file, create/advance the upload batch, hand the real work
        // to a Celery worker and return 202 immediately. What remains on this
        // request is just the multipart body transfer. This deliberately does
        // NOT scale the timeout up for centers — the previous 900000 override
        // only existed because parsing/validating happened inline in the
        // request, which is exactly the bottleneck that made a 22MB/320k-row
        // upload take down the server. Progress is followed by polling
        // (useUploadBatchPolling), not by holding a socket open.
        const timeout = 180000;
        return this.api.post(`${this._masterUploadBase(resource)}/bulk-upload/validate/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout,
        });
    }

    async confirmMasterUpload(resource, uploadId) {
        // Same reasoning as validateMasterUpload — confirm is now a cheap
        // state transition + task dispatch, not a synchronous re-parse.
        const timeout = 180000;
        const res = await this.api.post(`${this._masterUploadBase(resource)}/bulk-upload/confirm/`,
            { upload_id: uploadId }, { timeout });
        this.invalidateCache(this._masterUploadBase(resource));
        return res;
    }

    downloadMasterUploadErrorReport(uploadId) {
        return this.api.get(`/organization/upload-batches/${uploadId}/error-report/`, { responseType: 'blob' });
    }

    // ── Employee bulk upload — validate -> confirm flow on UserViewSet. ──
    // Reuses the shared validate/confirm BaseApi helpers; the error report
    // endpoint above is resource-agnostic, so it is reused as-is.
    downloadEmployeeUploadTemplate() {
        return this.api.get('/organization/users/employee-upload/template/', { responseType: 'blob' });
    }

    async validateEmployeeUpload(file) {
        const formData = new FormData();
        formData.append('file', file);
        return this.api.post('/organization/users/employee-upload/validate/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 180000,
        });
    }

    async confirmEmployeeUpload(uploadId) {
        const res = await this.api.post('/organization/users/employee-upload/confirm/', { upload_id: uploadId });
        this.invalidateCache('/organization/users');
        return res;
    }

    getMyDevices() {
        return this.get('/organization/devices/my_devices/');
    }

    revokeDevice(deviceId) {
        return this.post('/organization/devices/revoke_device/', { session_device_id: deviceId });
    }

    revokeAllOtherDevices() {
        return this.post('/organization/devices/revoke_all_other/');
    }

    setPrimaryDevice(deviceId) {
        return this.post('/organization/devices/set_primary/', { device_id: deviceId });
    }

    getAllDevices(params = {}, options = {}) {
        return this.get('/organization/devices/', params, {
            useCache: false,
            ...options,
        });
    }

    getDeviceFilterOptions() {
        return this.get('/organization/devices/filter_options/', {}, { useCache: false });
    }

    getUserDeviceSummary(userId) {
        return this.get('/organization/devices/user_device_summary/', { user_id: userId });
    }

    setMultiDeviceAccess(userId, allowMultipleDevices) {
        return this.post('/organization/devices/set_multi_device_access/', {
            user_id: userId, allow_multiple_devices: allowMultipleDevices,
        });
    }

    // deviceLimit: a whole number (>=1), or null/undefined to reset the
    // Employee/Guest user back to the standard limit (1).
    setDeviceLimit(userId, deviceLimit) {
        return this.post('/organization/devices/set_device_limit/', {
            user_id: userId, device_limit: deviceLimit,
        });
    }

    approveDevice(deviceId) {
        return this.post(`/organization/devices/${deviceId}/approve/`);
    }

    rejectDevice(deviceId) {
        return this.post(`/organization/devices/${deviceId}/reject/`);
    }

    blockDevice(deviceId) {
        return this.post(`/organization/devices/${deviceId}/block/`);
    }

    unblockDevice(deviceId) {
        return this.post(`/organization/devices/${deviceId}/unblock/`);
    }

    resetDevice(deviceId) {
        return this.post(`/organization/devices/${deviceId}/reset/`);
    }

    getDeviceHistory(deviceId) {
        return this.get(`/organization/devices/${deviceId}/history/`);
    }

    getUserDeviceHistory(userId) {
        return this.get('/organization/devices/user_history/', { user_id: userId });
    }

    removeDevice(deviceId) {
        return this.delete(`/organization/devices/${deviceId}/remove_device/`);
    }

    removeAllUserDevices(userId) {
        return this.post('/organization/devices/remove_user_devices/', { user_id: userId });
    }

    resetUserDevices(userId) {
        return this.post('/organization/devices/reset_user_devices/', { user_id: userId });
    }

    getDeviceDashboardSummary() {
        return this.get('/organization/devices/dashboard_summary/', {}, { useCache: false });
    }

    getDeviceUsageSummary(deviceId) {
        return this.get(`/organization/devices/${deviceId}/usage_summary/`);
    }

    getDeviceReports(params = {}) {
        return this.get('/organization/devices/reports/', params);
    }

    getPasswordExpiryList(params = {}) {
        return this.get('/auth/password-expiry/', params);
    }

    resetUserPasswordExpiry(userId, type = 'expiry') {
        return this.post('/auth/password-expiry/reset/', { user_id: userId, type });
    }

    getRouteHistory(params = {}, options = {}) {
        return this.get('/livetracking/route-history/', params, options);
    }

    getRouteDetail(sessionId) {
        // Same reasoning as getLiveDailyRoute: a replayed route detail is a
        // route that may not exist any more.
        return this.get(
            `/tracking/routes/detail/${sessionId}/`,
            {},
            { useCache: false, persistentCache: false },
        );
    }

    // ── Live Tracking (10s GPS route) ──
    // params: { date?: 'YYYY-MM-DD', employee_id?: id|code, simplified?: 0|1 }
    //
    // The route payload is the OFFICIAL trajectory: the backend has already
    // segmented it at every gap and map-matched each continuous segment onto
    // real roads. `map_matched` is therefore not a client choice any more - it
    // is the only correct rendering - and it is sent explicitly so the intent
    // is visible at the call site.
    //
    // Cache is bypassed on both layers on purpose. A stale route is worse than
    // no route: yesterday's geometry shown against today's date (or another
    // employee's, on a shared device) would look like a real route. Route data
    // is refetched, not replayed.
    getLiveDailyRoute(params = {}, options = {}) {
        return this.get(
            '/livetracking/daily/',
            { map_matched: '1', ...params },
            { useCache: false, persistentCache: false, ...options },
        );
    }

    getDailyTrackingSummary(params = {}) {
        return this.getTrackingDailySummary(params);
    }

    getActiveLiveSession() {
        return this.get('/livetracking/sessions/active/');
    }

    /**
     * The ONE authoritative distance for an employee + business day.
     *
     * Cheap (a few integers, no GPS payload) and the same value the route maps
     * draw, so a distance card and a map can never disagree. Scoped by the
     * caller and the IST business date; never cached or replayed, because a
     * stale distance looks exactly like a correct one.
     */
    getTrackingDailySummary(params = {}) {
        return this.get(
            '/livetracking/daily-summary/',
            params,
            { useCache: false, persistentCache: false },
        );
    }

    getCorrectionRequests(params = {}) {
        return this.get('/attendance/correction-requests/', params);
    }

    getMyCorrectionRequests() {
        return this.get('/attendance/correction-requests/my_requests/');
    }

    getPendingCorrections() {
        return this.get('/attendance/correction-requests/pending/');
    }

    getCorrectionRequestsPendingCount() {
        return this.get('/attendance/correction-requests/pending_count/');
    }

    createCorrectionRequest(data) {
        return this.post('/attendance/correction-requests/', data);
    }

    reviewCorrection(correctionId, action, comment = '') {
        return this.post(`/attendance/correction-requests/${correctionId}/review/`, { action, comment });
    }

    getCorrectionSettings() {
        return this.get('/attendance/correction-settings/', {}, { useCache: true });
    }

    updateCorrectionSettings(data) {
        return this.patch('/attendance/correction-settings/', data);
    }

    getNotificationSettings() {
        return this.get('/organization/notification-settings/');
    }

    updateNotificationSettings(settings) {
        return this.patch('/organization/notification-settings/', { settings });
    }

    getApprovalHierarchies() {
        return this.get('/organization/approval-hierarchies/');
    }

    getApprovalHierarchiesByType(requestType) {
        return this.get('/organization/approval-hierarchies/by_type/', { type: requestType });
    }

    createApprovalHierarchy(data) {
        return this.post('/organization/approval-hierarchies/', data);
    }

    updateApprovalHierarchy(id, data) {
        return this.patch(`/organization/approval-hierarchies/${id}/`, data);
    }

    deleteApprovalHierarchy(id) {
        return this.delete(`/organization/approval-hierarchies/${id}/`);
    }

    getApprovalRoutes(params = {}) {
        return this.get('/organization/approval-routes/', params);
    }

    getPendingApprovalRoutes() {
        return this.get('/organization/approval-routes/pending/');
    }

    processApprovalRoute(routeId, action, comments = '') {
        return this.post(`/organization/approval-routes/${routeId}/process/`, { action, comments });
    }

    getNotifications(params = {}) {
        return this.get('/organization/notifications/', params);
    }

    getUnreadNotificationCount(params = {}) {
        return this.get('/organization/notifications/unread_count/', params);
    }

    markNotificationRead(id) {
        return this.post(`/organization/notifications/${id}/mark_read/`);
    }

    markAllNotificationsRead() {
        return this.post('/organization/notifications/mark_all_read/');
    }

    clearReadNotifications() {
        return this.delete('/organization/notifications/clear_all/');
    }

    dismissNotification(id) {
        return this.post(`/organization/notifications/${id}/dismiss/`);
    }

    dismissAllNotifications() {
        return this.post('/organization/notifications/dismiss_all/');
    }

    // ── Punch Tracking Alerts (SuperAdmin monitoring) ───────────────────────
    getPunchTrackingAlerts(params = {}) {
        return this.get('/attendance/tracking-alerts/', params);
    }

    runPunchTrackingCheck() {
        return this.post('/attendance/tracking-alerts/run-check/');
    }

    getFeatures(options = { useCache: true }) {
        return this.get('/organization/features/', {}, options);
    }

    getRoleFeatures(roleId) {
        return this.get('/organization/role-features/', { role: roleId });
    }

    getRoleFeaturesByRole(roleId) {
        return this.get('/organization/role-features/by_role/', { role_id: roleId });
    }

    toggleRoleFeature(roleId, featureId, isEnabled) {
        return this.post('/organization/role-features/toggle/', {
            role_id: roleId,
            feature_id: featureId,
            is_enabled: isEnabled
        });
    }
    getRoleFeaturesByDesignation(roleId) {
        return this.get('/organization/role-features/by_designation/', { role_id: roleId });
    }
    toggleRoleFeatureForDesignation(roleId, featureId, designationId, isEnabled) {
        return this.post('/organization/role-features/toggle/', {
            role_id: roleId,
            feature_id: featureId,
            designation_id: designationId || null,
            is_enabled: isEnabled
        });
    }

    bulkUpdateRoleFeatures(roleId, featureIds, platform = null) {
        const body = { role: roleId, features: featureIds };
        if (platform) body.platform = platform;
        return this.post('/organization/role-features/bulk_update/', body);
    }

    getUserFeaturesByUser(userId) {
        return this.get('/organization/user-features/by_user/', { user_id: userId });
    }

    getUserFeaturesByRole(roleId) {
        return this.get('/organization/user-features/by_role/', { role_id: roleId });
    }

    toggleUserFeature(userId, featureId, isEnabled) {
        return this.post('/organization/user-features/toggle/', {
            user_id: userId,
            feature_id: featureId,
            is_enabled: isEnabled
        });
    }

    getDepartmentChoices() {
        return this.get('/organization/department-features/department_choices/', {}, { useCache: true });
    }

    getDepartmentFeaturesByDepartment(departmentName) {
        return this.get('/organization/department-features/by_department/', { department_name: departmentName });
    }

    toggleDepartmentFeature(departmentName, featureId, isEnabled) {
        return this.post('/organization/department-features/toggle/', {
            department_name: departmentName,
            feature_id: featureId,
            is_enabled: isEnabled
        });
    }

    bulkUpdateUserFeatures(userId, featureIds) {
        return this.post('/organization/user-features/bulk_update/', {
            user: userId,
            features: featureIds
        });
    }

    // Session Management - One User One Device
    getMySessions() {
        return this.get('/organization/sessions/my_sessions/');
    }

    getActiveSession() {
        return this.get('/organization/sessions/active_session/');
    }

    getAllSessions(params = {}) {
        return this.get('/organization/sessions/', params);
    }

    terminateSession(sessionId) {
        return this.post(`/organization/sessions/${sessionId}/terminate/`);
    }

    terminateAllUserSessions(userId) {
        return this.post('/organization/sessions/terminate_all/', { user_id: userId });
    }

    logoutCurrentSession() {
        return this.post('/organization/sessions/logout_current/');
    }

    logoutApi() {
        return this.post('/auth/logout/');
    }

    // Address Autocomplete
    getAddressSuggestions(query, limit = 5) {
        return this.get('/attendance/address/suggestions/', { q: query, limit });
    }

    getAddressDetails(placeId) {
        return this.get(`/attendance/address/details/${placeId}/`);
    }

    calculateDistance(fromAddress, toAddress) {
        return this.post('/attendance/address/calculate-distance/', {
            from_address: fromAddress,
            to_address: toAddress
        });
    }

    // Designation Master
    getDesignations(params = {}, options = { useCache: true }) {
        return this.get('/organization/designations/', params, options);
    }

    createDesignation(data) {
        return this.post('/organization/designations/', data);
    }

    updateDesignation(id, data) {
        return this.put(`/organization/designations/${id}/`, data);
    }

    deleteDesignation(id) {
        return this.delete(`/organization/designations/${id}/`);
    }

    updateDesignationPartial(id, data) {
        return this.patch(`/organization/designations/${id}/`, data);
    }

    reorderDesignations(orderedIds) {
        return this.post('/organization/designations/reorder/', { ordered_ids: orderedIds });
    }

    getDesignationsByGrade() {
        return this.get('/organization/designations/by_grade/', {}, { useCache: true });
    }

    getDesignationsByDepartment() {
        return this.get('/organization/designations/by_department/', {}, { useCache: true });
    }

    getDesignationGrades() {
        return this.get('/organization/designations/grades/', {}, { useCache: true });
    }

    getDesignationDepartments() {
        return this.get('/organization/designations/departments/', {}, { useCache: true });
    }

    // ── Master Data Management ──────────────────────────────────────────────
    getMasterCategories() {
        return this.get('/master-data/categories/', {}, { useCache: true });
    }

    searchMasterValues(categoryKey, query = '', limit = 20) {
        return this.get('/master-data/values/', { category: categoryKey, q: query, limit });
    }

    createMasterValue(categoryKey, name) {
        return this.post('/master-data/requests/', { category_key: categoryKey, requested_value: name });
    }

    incrementMasterUsage(valueId) {
        return this.post(`/master-data/values/${valueId}/increment_usage/`, {});
    }

    checkMasterDuplicate(categoryKey, name) {
        return this.get('/master-data/values/check_duplicate/', { category: categoryKey, name });
    }

    getMasterRequests(params = {}) {
        return this.get('/master-data/requests/', params);
    }

    approveMasterRequest(id, remarks = '') {
        return this.post(`/master-data/requests/${id}/approve/`, { remarks });
    }

    rejectMasterRequest(id, remarks = '') {
        return this.post(`/master-data/requests/${id}/reject/`, { remarks });
    }

    getPendingMasterRequests() {
        return this.get('/master-data/requests/pending/');
    }

    // ===================== HIERARCHY & APPROVAL ENGINE =====================
    // Zones / Regions
    getHierarchyZones() {
        return this.get('/hierarchy/zones/');
    }

    createHierarchyZone(data) {
        return this.post('/hierarchy/zones/', data);
    }

    updateHierarchyZone(id, data) {
        return this.patch(`/hierarchy/zones/${id}/`, data);
    }

    deleteHierarchyZone(id) {
        return this.delete(`/hierarchy/zones/${id}/`);
    }

    getHierarchyRegions(params = {}) {
        return this.get('/hierarchy/regions/', params);
    }

    createHierarchyRegion(data) {
        return this.post('/hierarchy/regions/', data);
    }

    updateHierarchyRegion(id, data) {
        return this.patch(`/hierarchy/regions/${id}/`, data);
    }

    deleteHierarchyRegion(id) {
        return this.delete(`/hierarchy/regions/${id}/`);
    }

    // Hierarchies
    getHierarchies(params = {}) {
        return this.get('/hierarchy/hierarchies/', params);
    }

    getHierarchy(id) {
        return this.get(`/hierarchy/hierarchies/${id}/`);
    }

    createHierarchy(data) {
        return this.post('/hierarchy/hierarchies/', data);
    }

    updateHierarchy(id, data) {
        return this.patch(`/hierarchy/hierarchies/${id}/`, data);
    }

    deleteHierarchy(id) {
        return this.delete(`/hierarchy/hierarchies/${id}/`);
    }

    getHierarchyTree(id) {
        return this.get(`/hierarchy/hierarchies/${id}/tree/`);
    }

    cloneHierarchy(id, name) {
        return this.post(`/hierarchy/hierarchies/${id}/clone/`, { name });
    }

    previewHierarchy(id, employeeId) {
        return this.post(`/hierarchy/hierarchies/${id}/preview/`, { employee_id: employeeId });
    }

    // Hierarchy Levels
    getHierarchyLevels(hierarchyId) {
        return this.get('/hierarchy/levels/', { hierarchy: hierarchyId });
    }

    createHierarchyLevel(data) {
        return this.post('/hierarchy/levels/', data);
    }

    updateHierarchyLevel(id, data) {
        return this.patch(`/hierarchy/levels/${id}/`, data);
    }

    deleteHierarchyLevel(id) {
        return this.delete(`/hierarchy/levels/${id}/`);
    }

    reorderHierarchyLevels(hierarchyId, orderedLevelIds) {
        return this.post('/hierarchy/levels/reorder/', { hierarchy: hierarchyId, ordered_level_ids: orderedLevelIds });
    }

    bulkImportHierarchyLevels(hierarchyId, levels) {
        return this.post('/hierarchy/levels/bulk_import/', { hierarchy: hierarchyId, levels });
    }

    bulkExportHierarchyLevels(hierarchyId) {
        return this.get('/hierarchy/levels/bulk_export/', { hierarchy: hierarchyId });
    }

    // Approval Matrix
    getApprovalMatrices(params = {}) {
        return this.get('/hierarchy/matrices/', params);
    }

    createApprovalMatrix(data) {
        return this.post('/hierarchy/matrices/', data);
    }

    updateApprovalMatrix(id, data) {
        return this.patch(`/hierarchy/matrices/${id}/`, data);
    }

    deleteApprovalMatrix(id) {
        return this.delete(`/hierarchy/matrices/${id}/`);
    }

    // Approval Requests (history / timeline / pending)
    getApprovalRequests(params = {}) {
        return this.get('/hierarchy/requests/', params);
    }

    getApprovalRequest(id) {
        return this.get(`/hierarchy/requests/${id}/`);
    }

    getApprovalRequestTimeline(id) {
        return this.get(`/hierarchy/requests/${id}/timeline/`);
    }

    getMyPendingApprovals() {
        return this.get('/hierarchy/requests/pending_for_me/');
    }

    getApprovalRequestBySource(moduleCode, sourceId) {
        return this.get('/hierarchy/requests/by_source/', { module_code: moduleCode, source_id: sourceId });
    }

    // Delegation
    getDelegations(params = {}) {
        return this.get('/hierarchy/delegations/', params);
    }

    createDelegation(data) {
        return this.post('/hierarchy/delegations/', data);
    }

    updateDelegation(id, data) {
        return this.patch(`/hierarchy/delegations/${id}/`, data);
    }

    deleteDelegation(id) {
        return this.delete(`/hierarchy/delegations/${id}/`);
    }

    // Escalation Rules
    getEscalationRules(params = {}) {
        return this.get('/hierarchy/escalation-rules/', params);
    }

    createEscalationRule(data) {
        return this.post('/hierarchy/escalation-rules/', data);
    }

    updateEscalationRule(id, data) {
        return this.patch(`/hierarchy/escalation-rules/${id}/`, data);
    }

    deleteEscalationRule(id) {
        return this.delete(`/hierarchy/escalation-rules/${id}/`);
    }

    // Dashboard
    getHierarchyDashboardSummary() {
        return this.get('/hierarchy/dashboard/summary/');
    }

    // ── Employee Reporting Tree (Super Admin) ──
    getEmployeeHierarchy(search = '', role = '') {
        const params = {};
        if (search) params.search = search;
        if (role) params.role = role;
        return this.get('/hierarchy/employee-tree/', params, { useCache: false });
    }
    getEmployeeHierarchyTree() {
        return this.get('/hierarchy/employee-tree/tree/', {}, { useCache: false });
    }
    assignReportingManager(employeeId, managerId, reason = '', options = {}) {
        const { isPrimary = true, effectiveFrom = null, effectiveUntil = null } = options;
        return this.post('/hierarchy/employee-tree/assign/', {
            employee_id: employeeId,
            manager_id: managerId === null ? null : managerId,
            reason,
            is_primary: isPrimary,
            effective_from: effectiveFrom,
            effective_until: effectiveUntil,
        });
    }
    getEmployeeHierarchyHistory(employeeId) {
        return this.get(`/hierarchy/employee-tree/history/${employeeId}/`, {}, { useCache: false });
    }
    succeedEmployee(departingEmployeeId, replacementEmployeeId, reason = '') {
        return this.post('/hierarchy/employee-tree/succeed/', {
            departing_employee_id: departingEmployeeId,
            replacement_employee_id: replacementEmployeeId,
            reason,
        });
    }

    // Legal & Privacy (apps/legal) — the single source of the Terms, Privacy
    // Policy and Location & Tracking Notice for web and mobile.
    getLegalDocuments() { return this.get('/legal/documents/', {}, { useCache: false }); }
    getLegalStatus() { return this.get('/legal/status/', {}, { useCache: false }); }
    acknowledgeLegal(payload) { return this.post('/legal/acknowledge/', payload); }
    getMyAcknowledgements() { return this.get('/legal/my-acknowledgements/', {}, { useCache: false }); }
    getLegalVersions(params = {}) { return this.get('/legal/admin/versions/', params, { useCache: false }); }
    createLegalVersion(data) { return this.post('/legal/admin/versions/', data); }
    updateLegalVersion(id, data) { return this.patch(`/legal/admin/versions/${id}/`, data); }
    deleteLegalVersion(id) { return this.delete(`/legal/admin/versions/${id}/`); }
    publishLegalVersion(id) { return this.post(`/legal/admin/versions/${id}/publish/`); }
    validateLegalVersion(id) { return this.get(`/legal/admin/versions/${id}/validate/`, {}, { useCache: false }); }
    submitLegalVersion(id) { return this.post(`/legal/admin/versions/${id}/submit_review/`); }
    approveLegalVersion(id) { return this.post(`/legal/admin/versions/${id}/approve/`); }
    rejectLegalVersion(id, reason = '') { return this.post(`/legal/admin/versions/${id}/reject/`, { reason }); }
    retireLegalVersion(id) { return this.post(`/legal/admin/versions/${id}/retire/`); }
    downloadLegalVersion(id) { return this.api.get(`/legal/admin/versions/${id}/download/`, { responseType: 'blob' }); }
    getLegalReport(params = {}) { return this.get('/legal/admin/report/', params, { useCache: false }); }
    getRetentionPolicies() { return this.get('/legal/admin/retention/', {}, { useCache: false }); }
    saveRetentionPolicy(data) {
        return data.id ? this.patch(`/legal/admin/retention/${data.id}/`, data) : this.post('/legal/admin/retention/', data);
    }

    // Employee Team & Case Overview (hierarchy/overview.py) — server-side
    // aggregates over the reporting tree; all take { date_from, date_to }.
    getHierarchyOverview(employeeId, params = {}) {
        return this.get(`/hierarchy/team-overview/${employeeId}/`, params, { useCache: false });
    }
    getHierarchyReports(employeeId, params = {}) {
        return this.get(`/hierarchy/team-overview/${employeeId}/reports/`, params, { useCache: false });
    }
    getHierarchyActivity(employeeId, params = {}) {
        return this.get(`/hierarchy/team-overview/${employeeId}/activity/`, params, { useCache: false });
    }
    getLoanTimeline(loanId) {
        return this.get('/hierarchy/team-overview/loan-timeline/', { loan_id: loanId }, { useCache: false });
    }
}

if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
        const api = ScalableAPI.getInstance();
        api.processOfflineQueue();
    });
}

export default ScalableAPI.getInstance();
